import type {
  InvestorCase,
  InvestorEngagementSummary,
  Page,
  StaffCase,
  StaffEngagementSummary,
} from '@fruuit/shared'
import { useApiQuery } from '../../api/hooks'

/** Query keys shared by lane D screens, so mutations can invalidate them. */
export const keys = {
  engagements: ['engagements'] as const,
  list: (view: 'investor' | 'staff', params: Record<string, string>) =>
    ['engagements', 'list', view, params] as const,
  case: (view: 'investor' | 'staff', id: string) => ['engagements', 'case', view, id] as const,
  questions: ['questions'] as const,
  participation: ['participation-requests'] as const,
  clientActions: ['client-actions'] as const,
  inbox: ['inbox'] as const,
  savedViews: ['saved-views'] as const,
}

/** Everything a lane D mutation may change (lists, cases, inbox). */
export const LANE_D_KEYS = [
  keys.engagements,
  keys.questions,
  keys.participation,
  keys.clientActions,
  keys.inbox,
]

export interface EngagementListParams {
  status?: string
  stage?: string
  theme?: string
  q?: string
  issuer_id?: string
  cursor?: string
}

function clean(params: EngagementListParams): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [key, value] of Object.entries(params)) if (value) out[key] = value
  return out
}

export function useInvestorEngagements(params: EngagementListParams) {
  const query = clean(params)
  return useApiQuery<Page<InvestorEngagementSummary>>(
    keys.list('investor', query),
    '/engagements',
    { query: { ...query, view: 'investor', limit: 25 }, placeholderData: (prev) => prev },
  )
}

export function useStaffEngagements(params: EngagementListParams) {
  const query = clean(params)
  return useApiQuery<Page<StaffEngagementSummary>>(keys.list('staff', query), '/engagements', {
    query: { ...query, view: 'staff', limit: 25 },
    placeholderData: (prev) => prev,
  })
}

export function useInvestorCase(id: string) {
  return useApiQuery<InvestorCase>(keys.case('investor', id), `/engagements/${id}`, {
    query: { view: 'investor' },
  })
}

export function useStaffCase(id: string) {
  return useApiQuery<StaffCase>(keys.case('staff', id), `/engagements/${id}`, {
    query: { view: 'staff' },
  })
}
